<script setup>
import { inject, computed, ref } from 'vue'
import { copyString } from '@/utils'
import { getShare } from '@/api/app.js'

const props = defineProps({
  size: { type: String, default: null },
  shareId: { type: [String, Number], default: null }
})

const popover2Ref = inject('popover2Ref')
const isMobile = computed(() => ['s', 'xs'].includes(props.size))
const defaultShare = [
  {
    icon: 'icon_share_facebook',
    title: '透過 Facebook 分享',
    type: 'facebook'
  },
  {
    icon: 'icon_share_whatsapp',
    title: '透過 WhatsApp 分享',
    type: 'whatsApp'
  },
  {
    icon: 'icon_share_link',
    title: '複製連結',
    type: 'copy'
  }
]

const option = {
  facebook: 1,
  whatsApp: 2,
  copy: 3
}
const handleFetchShare = (type) => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    try {
      const res = await getShare({
        share_id: props.shareId,
        type: option[type]
      })
      if (res.code !== 0) throw new Error(res.message)
      // console.log(res?.data, 'getShare')
      const shareInfo = res?.data
      if (!shareInfo) throw new Error('未获取到数据')
      let str = `${shareInfo.title}\r\n${shareInfo.link ? shareInfo.link : ''}`
      if (shareInfo.content) str = str + `\r\n====================\r\n${shareInfo.content}`

      resolve({ str, data: shareInfo })
    } catch (error) {
      reject(error)
    }
  })
}

const show = ref(false)
const tip = ref('')
// 处理复制链接
const handleCopyShare = async (type) => {
  try {
    const res = await handleFetchShare(type)
    await copyString(res?.str)
    tip.value = '已複製連結'
    show.value = true
  } catch (error) {
    console.warn()
    tip.value = '操作失敗'
    show.value = true
  }
}

const handleWhatsApp = async (type) => {
  try {
    const res = await handleFetchShare(type)
    setTimeout(() => {
      window.open(
        `https://api.whatsapp.com/send?text=${encodeURIComponent(res?.str || '')}`,
        '_blank'
      )
    })
  } catch (error) {
    tip.value = '操作失敗'
    show.value = true
  }
}

const handleFBShare = async (type) => {
  try {
    const res = await handleFetchShare(type)
    setTimeout(() => {
      window.FB.ui({
        method: 'share',
        href: res?.data?.link ? res.data.link : ''
      })
    })
  } catch (error) {
    tip.value = '操作失敗'
    show.value = true
  }
}

const handleClick = (item) => {
  popover2Ref.value?.setShow?.(false)
  switch (item.type) {
    case 'copy':
      handleCopyShare(item.type)
      break
    case 'facebook':
      handleFBShare(item.type)
      break
    case 'whatsApp':
      handleWhatsApp(item.type)
      break
  }
}
</script>

<template>
  <div
    class="tvb-article-share tvb-hairline--surround"
    :class="[size ? 'share-' + size : '', { 'tvb-article-share-hover': !isMobile }]"
  >
    <div
      v-for="item in defaultShare"
      :key="item.type"
      class="tvb-article-share-item"
      @click="handleClick(item)"
    >
      <div class="tvb-article-share-item_icon">
        <tvb-svg-icon :name="item.icon" />
      </div>
      <p class="tvb-article-share-item_text">{{ item.title }}</p>
    </div>
    <tvb-toast
      v-model:show="show"
      :size="size"
      :message="tip"
      :position="!isMobile ? 'absolute' : ''"
      :teleportDis="!isMobile"
    ></tvb-toast>
  </div>
</template>

<style lang="scss" scoped>
.tvb-article-share {
  position: relative;
  width: 224px;
  padding: 12px 0;
  border-radius: 10px;
  --tvb-hairline-border-radius: calc(2 * 10px);
  background-color: #fff;
  box-shadow: 0px 4px 14px -4px rgba(129, 141, 149, 0.4);
  --tvb-share-sub-h: 44px;
  --tvb-share-sub-padding: 0 0 0 16px;
  --tvb-share-sub-ifs: 28px;
  --tvb-share-sub-imr: 10px;
  --tvb-share-sub-fs: 16px;
  --tvb-share-sub-color: #333;
  --tvb-share-sub-lh: 22px;
  --tvb-share-sub-ls: 0.5px;
  --tvb-share-sub-hover-bgc: #f5f6f7;

  .tvb-article-share-item {
    display: flex;
    align-items: center;
    padding: var(--tvb-share-sub-padding);
    height: var(--tvb-share-sub-h);
    transition: background-color 0.1s linear;
    line-height: 0;
    cursor: pointer;

    .tvb-article-share-item_icon {
      font-size: var(--tvb-share-sub-ifs);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: var(--tvb-share-sub-imr);
      user-select: none;
    }

    .tvb-article-share-item_text {
      font-size: var(--tvb-share-sub-fs);
      font-weight: 400;
      color: var(--tvb-share-sub-color);
      line-height: var(--tvb-share-sub-lh);
      letter-spacing: var(--tvb-share-sub-ls);
    }
  }

  &.tvb-article-share-hover {
    .tvb-article-share-item:hover {
      background-color: var(--tvb-share-sub-hover-bgc);
    }
  }

  &.share-s,
  &.share-xs {
    width: 196px;
    padding: 10px 0;
    border-radius: 8px;
    --tvb-hairline-border-radius: calc(2 * 8px);
    box-shadow: 0px 2px 8px -2px rgba(129, 141, 149, 0.4);
    --tvb-share-sub-h: 40px;
    --tvb-share-sub-padding: 0 0 0 14px;
    --tvb-share-sub-ifs: 24px;
    --tvb-share-sub-color: #555;
    --tvb-share-sub-imr: 6px;
    --tvb-share-sub-fs: 15px;
    --tvb-share-sub-ls: 0px;
  }
}
</style>
